import React from 'react';
import { useCall } from './CallProvider';
import useMediaQuery from '../../utils/useMediaQuery';
import { miniLabel, ringOpacity, RING_CONIC, C, fmtTimer } from './callModel';
import { IC } from './CallLayer';

/**
 * Phone, voice (handoff 1a): a full-width black bar, 48px, under the status bar and ABOVE the
 * app header — it pushes the layout down, never overlaps. Green dot · 28px ring avatar
 * (opacity follows their level) · `REZA · 2:14` over `TAP TO RETURN` · mute · END. Rendered by
 * AppChrome inside the sticky chrome, so useTopBarHeight counts it.
 */
export default function CallMiniBar() {
  const ctx = useCall();
  const phone = useMediaQuery('(max-width: 767px)');
  const call = ctx?.call;
  if (!call || call.view !== 'mini' || call.video || !phone) return null;
  const one = call.peers.find((p) => p.state !== 'left') || call.peers[0];
  return (
    <div
      data-call-bar
      data-testid="call-bar"
      role="button"
      tabIndex={0}
      onClick={(e) => ctx.restore(e.currentTarget)}
      onKeyDown={(e) => { if (e.key === 'Enter') ctx.restore(e.currentTarget); }}
      className="font-mono"
      style={{ width: '100%', background: '#000', color: '#fff', display: 'flex', alignItems: 'center', gap: 10, padding: 'env(safe-area-inset-top, 0px) 10px 0 16px', height: 'calc(48px + env(safe-area-inset-top, 0px))', boxSizing: 'border-box', cursor: 'pointer', textAlign: 'left' }}
    >
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.live, flexShrink: 0 }} />
      <span style={{ position: 'relative', width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: RING_CONIC, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: ringOpacity(one?.level), transition: 'opacity 140ms linear' }}>
        {one?.avatarUrl
          ? <img src={one.avatarUrl} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', border: '2px solid #000', boxSizing: 'border-box' }} />
          : <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#FFCCE6', border: '2px solid #000', boxSizing: 'border-box' }} />}
      </span>
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span className="font-bold truncate" style={{ fontSize: 11, letterSpacing: '.1em' }}>{miniLabel(call, ctx.now)}</span>
        <span style={{ fontSize: 8, letterSpacing: '.12em', color: 'rgba(255,255,255,.6)' }}>TAP TO RETURN</span>
      </span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); ctx.toggleMute(); }}
        aria-label={call.muted ? 'Unmute' : 'Mute'}
        data-testid="bar-mute"
        style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid rgba(255,255,255,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: call.muted ? '#fff' : 'transparent', color: call.muted ? '#000' : '#fff', padding: 0, cursor: 'pointer' }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="square"><path d={call.muted ? IC.micOff : IC.mic} /></svg>
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); ctx.hangup(); }}
        data-testid="bar-end"
        className="font-bold"
        style={{ height: 32, padding: '0 14px', borderRadius: 999, border: 0, background: C.end, color: '#fff', fontSize: 10, letterSpacing: '.1em', cursor: 'pointer' }}
      >
        END
      </button>
    </div>
  );
}

/** Desktop (1d): the black `● ON A CALL · 2:14` pill in the top nav whenever a call is minimized. */
export function CallNavPill() {
  const ctx = useCall();
  const phone = useMediaQuery('(max-width: 767px)');
  const call = ctx?.call;
  if (!call || call.view !== 'mini' || phone) return null;
  return (
    <button
      type="button"
      onClick={(e) => ctx.restore(e.currentTarget)}
      data-testid="call-nav-pill"
      className="font-mono font-bold"
      style={{ height: 34, padding: '0 14px 0 10px', borderRadius: 999, border: 0, background: '#000', color: '#fff', display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, letterSpacing: '.1em', cursor: 'pointer', flexShrink: 0 }}
    >
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.live }} />ON A CALL · {fmtTimer(call.startedAt, ctx.now)}
    </button>
  );
}
